import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Milestone, MilestoneStartDecision, ProjectFundingStatus } from '../types';
import MilestoneStartSelector from './MilestoneStartSelector';
import Modal from './Modal';
import { Notice } from './Notice';
import { contractConfigApi } from '../services/api';
import { saveErrorText } from '../utils/apiError';

interface MilestoneStartModalProps {
  projectId?: string;
  milestones: Milestone[];
  userRole: 'company' | 'person';
  onSave: (decisions: MilestoneStartDecision[], acceptFundingRisk?: boolean) => Promise<void>;
  onClose: () => void;
}

export default function MilestoneStartModal({
  milestones,
  projectId,
  userRole,
  onSave,
  onClose
}: MilestoneStartModalProps) {
  const { t, i18n } = useTranslation();
  const [decisions, setDecisions] = useState<MilestoneStartDecision[] | undefined>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fundingMissingOre, setFundingMissingOre] = useState<number | null>(projectId ? null : 0);
  const [funding, setFunding] = useState<ProjectFundingStatus | null>(null);
  const [acceptFundingRisk, setAcceptFundingRisk] = useState(false);
  const single = milestones.length === 1 ? milestones[0] : null;
  const canConfirmStart = single &&
    (single.itemType ?? 'milestone') === 'milestone' &&
    !single.workStartedAt && (!single.deliveryStatus || single.deliveryStatus === 'not_started') &&
    !(single.companyAttestation === 'approved' && single.personAttestation === 'approved') &&
    (userRole === 'company' || !single.companyStartDecision);
  // The stage was already chosen in the timeline. Only this explicit
  // confirmation saves its start; opening/cancelling the dialog does nothing.
  const decisionsToSave = canConfirmStart
    ? [{ milestoneId: single.id, started: true }]
    : decisions;

  const startingIds = decisionsToSave?.filter(decision => decision.started).map(decision => decision.milestoneId).sort().join(',') ?? '';
  useEffect(() => {
    if (!projectId) return;
    let active = true;
    setFundingMissingOre(null);
    setAcceptFundingRisk(false);
    void contractConfigApi.getProjectFunding(projectId, startingIds ? startingIds.split(',') : undefined).then(funding => {
      if (active) { setFunding(funding); setFundingMissingOre(funding.account?.missingAmountOre ?? 0); }
    }).catch(() => { if (active) setError(t('projectFunding.loadError')); });
    return () => { active = false; };
  }, [projectId, startingIds, t]);

  const starting = decisionsToSave?.some(decision => decision.started) === true;
  const fundingRiskRequired = starting && fundingMissingOre !== null && fundingMissingOre > 0;
  const awaitingFundingCheck = starting && fundingMissingOre === null;

  const money = (ore: number) => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'SEK' }).format(ore / 100);
  const selectedFunding = funding?.milestones?.filter(stage => startingIds.split(',').includes(stage.milestoneId)) ?? [];
  const stageAmount = selectedFunding.reduce((sum, stage) => sum + stage.afterRotAmountOre, 0);
  const stageMissing = selectedFunding.reduce((sum, stage) => sum + (stage.uncoveredAmountOre ?? 0), 0);
  const riskTitle = single && stageMissing > 0 ? t('milestoneStarts.fundingRisk.title', { number: single.milestoneNumber }) : t('milestoneStarts.fundingRisk.titleMultiple');

  const save = async () => {
    if (saving || !decisionsToSave?.length || awaitingFundingCheck || (fundingRiskRequired && !acceptFundingRisk)) return;
    setSaving(true);
    setError('');
    try {
      if (fundingRiskRequired && acceptFundingRisk) await onSave(decisionsToSave, true);
      else await onSave(decisionsToSave);
      onClose();
    } catch (saveError) {
      // The choice stays, so the user can try again (ClickUp 869fgxq45).
      setError(saveErrorText(t, saveError, 'milestoneStarts.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      zIndex={1060}
      headerClassName="objection-header"
      dialogClassName="objection-dialog funding-start-dialog"
      fullscreenBelow="sm"
      onClose={() => { if (!saving) onClose(); }}
      title={(
        <>
          <i className="feather-play-circle me-2" aria-hidden="true"></i>
          {fundingRiskRequired ? riskTitle : single ? t('milestoneStarts.modalSingleTitle') : t('milestoneStarts.modalTitle')}
        </>
      )}
      footer={(
        <div className="modal-action-row">
          <button type="button" className={fundingRiskRequired ? "btn btn-outline-primary" : "btn btn-outline-secondary"} onClick={onClose} disabled={saving}>
            {t(fundingRiskRequired ? 'milestoneStarts.fundingRisk.wait' : 'common.cancel')}
          </button>
          <button
            type="button"
            className={fundingRiskRequired ? acceptFundingRisk ? "btn btn-warning" : "btn btn-secondary" : "btn btn-primary"}
            onClick={() => void save()}
            disabled={saving || !decisionsToSave?.length || awaitingFundingCheck || (fundingRiskRequired && !acceptFundingRisk)}
          >
            <i className="feather-check me-1" aria-hidden="true"></i>
            {saving ? t('milestoneStarts.saving')
              : fundingRiskRequired ? single ? t('milestoneStarts.fundingRisk.startAnyway', { number: single.milestoneNumber }) : t('milestoneStarts.fundingRisk.startAnywayMultiple')
              : canConfirmStart ? t('milestoneStarts.confirmStart')
                : single ? t('milestoneStarts.saveSingle') : t('milestoneStarts.save')}
          </button>
        </div>
      )}
    >
      {fundingRiskRequired && <div data-testid="funding-start-warning">
        <Notice level="warning" title={riskTitle} className="funding-start-warning" testId="funding-start-notice">
          <p className="text-explain">{t('milestoneStarts.fundingRisk.consequence')}</p>
          {selectedFunding.length > 0 && <dl className="row mb-3">
            <dt className="col-12 col-sm-7">{t('milestoneStarts.fundingRisk.stageAmount')}</dt><dd className="col-12 col-sm-5 text-sm-end text-nowrap">{money(stageAmount)}</dd>
            <dt className="col-12 col-sm-7">{t('milestoneStarts.fundingRisk.covered')}</dt><dd className="col-12 col-sm-5 text-sm-end text-nowrap">{money(Math.max(stageAmount - stageMissing, 0))}</dd>
            <dt className="col-12 col-sm-7">{t('milestoneStarts.fundingRisk.missing')}</dt><dd className="col-12 col-sm-5 text-sm-end text-nowrap fw-bold">{money(stageMissing)}</dd>
          </dl>}
          <p className="text-explain">{t('milestoneStarts.fundingRisk.requirementMissing', { amount: money(fundingMissingOre ?? 0) })}</p>
          <p className="text-explain">{t('milestoneStarts.fundingRisk.registeredFunds')}</p>
          <p className="text-explain mb-0">{t('milestoneStarts.fundingRisk.immediateNotice')}</p>
        </Notice>
        <label className="form-check d-flex gap-2 align-items-start my-3">
          <input type="checkbox" className="form-check-input" checked={acceptFundingRisk} onChange={event => setAcceptFundingRisk(event.target.checked)} />
          <span>{t('milestoneStarts.fundingRisk.acceptance')}</span>
        </label>
        <p className="text-explain">{t('milestoneStarts.fundingRisk.logged')}</p>
      </div>}
      {canConfirmStart ? (
        <div data-testid="milestone-start-confirmation">
          <p className="fw-semibold">{t('milestoneStarts.confirmStartQuestion', { number: single.milestoneNumber })}</p>
          <p>{single.description}</p>
          <p className="small text-muted mb-0">{t('milestoneStarts.selectorManualHint')}</p>
        </div>
      ) : (
        <MilestoneStartSelector
          milestones={milestones}
          userRole={userRole}
          context="manual"
          disabled={saving}
          onDecisionsChange={setDecisions}
        />
      )}
      {error && (
        <div className="alert alert-danger mt-3 mb-0" role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
